import { Suspense, createContext, useRef, useMemo, useContext } from 'react';
import { node, any, bool } from 'prop-types';
import LoadingBar from 'react-top-loading-bar';
import colors from '../../mixins/colorMap';
import ErrorBoundary from '../ErrorBoundary';
import LoadingMessage from '../LoadingMessage';
import style from './LoadBoundary.module.scss';

const LoadContext = createContext({ add: () => {}, remove: () => {}, root: false });

const LoadBoundary = ({
  children,
  fallback = null,
  center = false,
  loadingBar: showLoadingBarForUpdates = false,
  root = false,
}) => {
  const parentCtx = useLoadContext();
  const loadingBar = useRef();
  const updatingInstances = useRef(new Set());

  let el = fallback || (
    <div className={style.container}>
      <LoadingMessage center={center} root={root} useLogoAnimation={root} />
    </div>
  );
  if (import.meta.env.VITE_ENV === 'test') {
    el = (
      <div data-testid="suspense-spinner" data-trackingid="suspense-spinner">
        {el}
      </div>
    );
  }

  function add(promise) {
    if (!showLoadingBarForUpdates && parentCtx) {
      parentCtx.add(promise);
    }

    const instances = updatingInstances.current;

    if (!instances || !loadingBar.current) return;

    const shouldStart = instances.size === 0;
    instances.add(promise);

    if (shouldStart) {
      loadingBar.current.continousStart(20);
    }
  }

  function remove(promise) {
    if (!showLoadingBarForUpdates && parentCtx) {
      parentCtx.remove(promise);
    }

    const instances = updatingInstances.current;

    if (!instances || !loadingBar.current) return;

    instances.delete(promise);

    if (instances.size === 0) {
      loadingBar.current.complete();
    }
  }

  const ctx = useMemo(
    () => ({
      add,
      remove,
      root,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      {showLoadingBarForUpdates && (
        <div className={style.loadingBarWrapper}>
          <LoadingBar
            ref={loadingBar}
            className={style.loadingBar}
            height={3}
            color={colors['Mid Neutral 3']}
          />
        </div>
      )}
      <LoadContext.Provider value={ctx}>
        <ErrorBoundary center={center} root={root}>
          <Suspense fallback={el}>{children}</Suspense>
        </ErrorBoundary>
      </LoadContext.Provider>
    </>
  );
};

LoadBoundary.propTypes = {
  children: node,
  fallback: any,
  center: bool,
  loadingBar: bool,
  root: bool,
};

function useLoadContext() {
  return useContext(LoadContext);
}

// Export named exports first for Fast Refresh compatibility
export { LoadContext, useLoadContext };
// Then export the default
export default LoadBoundary;
